/**
 * Integrytas Group - Common Security Module (CSM)
 * PresentationControls — header gear icon with density, font-size, and theme panel.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings } from 'lucide-react';
import { usePresentation } from '../../lib/use_presentation';
import type { Density, FontSize } from '../../lib/use_presentation';

export function PresentationControls() {
  const { t }   = useTranslation();
  const pref    = usePresentation();
  const [open, setOpen]   = useState(false);
  const panelRef          = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const Option = <T extends string>({
    value, current, onSelect, label,
  }: { value: T; current: T; onSelect: (v: T) => void; label: string }) => (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={[
        'flex-1 rounded border py-1 text-csm-xs font-medium transition-colors',
        current === value
          ? 'border-primary bg-primary text-white'
          : 'border-csm-border bg-white text-csm-ink-soft hover:bg-csm-surface-alt',
      ].join(' ')}
    >
      {label}
    </button>
  );

  return (
    <div ref={panelRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={t('presentation.controls_label')}
        title={t('presentation.controls_label')}
        className="flex h-7 w-7 items-center justify-center rounded
                   text-white/80 hover:bg-white/10 hover:text-white"
      >
        <Settings className="h-4 w-4" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-40 w-64 rounded border
                     border-csm-border bg-white shadow-xl p-4"
        >
          <p className="mb-3 text-csm-xs font-semibold uppercase tracking-wider text-csm-muted">
            {t('presentation.panel_title')}
          </p>

          {/* Density */}
          <div className="mb-3">
            <p className="mb-1.5 text-csm-xs text-csm-ink-soft font-medium">
              {t('presentation.density_label')}
            </p>
            <div className="flex gap-1.5">
              <Option<Density>
                value="compact"
                current={pref.density}
                onSelect={pref.setDensity}
                label={t('presentation.density_compact')}
              />
              <Option<Density>
                value="comfortable"
                current={pref.density}
                onSelect={pref.setDensity}
                label={t('presentation.density_comfortable')}
              />
            </div>
          </div>

          {/* Font size */}
          <div className="mb-3">
            <p className="mb-1.5 text-csm-xs text-csm-ink-soft font-medium">
              {t('presentation.font_size_label')}
            </p>
            <div className="flex gap-1.5">
              <Option<FontSize>
                value="small"
                current={pref.fontSize}
                onSelect={pref.setFontSize}
                label={t('presentation.font_small')}
              />
              <Option<FontSize>
                value="medium"
                current={pref.fontSize}
                onSelect={pref.setFontSize}
                label={t('presentation.font_medium')}
              />
              <Option<FontSize>
                value="large"
                current={pref.fontSize}
                onSelect={pref.setFontSize}
                label={t('presentation.font_large')}
              />
            </div>
          </div>

          {/* Theme selection lives in the dedicated Theme selector (header). */}
        </div>
      )}
    </div>
  );
}
